import React, { useContext, useEffect, useState } from "react";
import {
  StyledContainer,
  StyledLastReadingSquare,
  StyledReadingText,
  StyledReadingInfoText,
  StyledSubContainer,
  StyledTableContainer
} from "./Vitals.styles";
import { useParams, useNavigate } from "react-router-dom";
import API from "api";
import Session from "helpers/session";
import {
  associateReadingType,
  formatReading,
  toLocalTime
} from "helpers/variables";
import { MeasurementProp } from "components/shared/Timeline/Timeline";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "context/Provider";
import SimpleGrid, {
  BodyProps,
  CellProps
} from "components/shared/SimpleGrid/SimpleGrid";
import { Box, Stack } from "@mui/material";
import moment from "moment";
import {
  ActionsInterface,
  EncounterInterface
} from "context/initialState/Encounter";

export default function Vitals() {
  const { t } = useTranslation();
  const { config, readingsDispatch } = useContext(GlobalContext);
  const [patientReadings, setPatientReadings] = useState<MeasurementProp[]>([]);
  const { memberId } = useParams();
  const navigate = useNavigate();
  const encounterTableHeaders: CellProps[] = [
    { content: "Time", align: "left" },
    { content: "Encounter Type", align: "center" },
    { content: "Action", align: "center" }
  ];
  const [encounterTableBody, setEncounterTableBody] = useState<any>();
  const [totalEncounterList, setTotalEncounterList] = useState<number>(0);
  const currentPath = window.location.pathname;

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    API.kore.readings
      .getRecentReadings(
        memberId || "",
        Session.crackCookieToken("coordinator")
      )
      .then((readings: any) => {
        let pulseVital = () => {
          const { bp, spo2, hrt } = readings;
          if (spo2 && !hrt) {
            return displayReadings.push({
              id: spo2.id,
              type: "Heart Rate",
              value: `${spo2.metrics?.pulse} bpm`,
              date: moment(spo2.measuredAt).format("MMM DD, HH:mm"),
              year: moment(spo2.measuredAt).format("YYYY")
            });
          } else if (bp && !hrt) {
            return displayReadings.push({
              id: bp.id,
              type: "Heart Rate",
              value: `${bp.metrics?.pulse} bpm`,
              date: moment(bp.measuredAt).format("MMM DD, HH:mm"),
              year: moment(bp.measuredAt).format("YYYY")
            });
          }
        };
        const displayReadings = [];
        console.log(readings);
        for (let type in readings) {
          if (readings[type]) {
            displayReadings.push(
              formatReading(readings[type], config?.basicOptions.units)
            );
          }
        }
        pulseVital();
        setPatientReadings(displayReadings);
      });
    populateEncounters();
  }, []);

  const populateEncounters = () => {
    API.kore.encounters
      .getList(Session.crackCookieToken("coordinator"))
      .then((_encounters) => {
        const today = new Date();
        const adjustedDate = moment(today.setDate(today.getDate() - 30)).format(
          "MM/DD/YYYY"
        );

        const freshList: EncounterInterface[] =
          _encounters.items?.filter((en: EncounterInterface) => {
            const individualDate = moment(en.day).format("MM/DD/YYYY");

            return individualDate >= adjustedDate;
          }) || [];

        const membersEncounterList = freshList
          ?.map((fl) => {
            return fl.touchPoints.filter((tp) => {
              return tp.referenceId === memberId;
            });
          })
          .flat();

        const membersEncounters = membersEncounterList
          .flat()
          .map((mel) => {
            return mel.interactions;
          })
          .flat();

        const totalActions = membersEncounters
          .map((me) => {
            if (me.actions.length !== 0) {
              return me.actions;
            }
          })
          .flat();
        const actions = totalActions.filter((ta) => {
          return ta !== undefined;
        });
        setEncounterTableBody(actions.reverse());
        setTotalEncounterList(actions.length);
      });
  };

  const gotoReadings = (type: string) => {
    if (type === "Pulse Oxygen") {
      type = "oxygensaturation";
    }
    navigate(
      `/patient/history/${type.toLowerCase().replace(" ", "")}/list/${memberId}`
    );
  };

  const generateList = (rawList: ActionsInterface[]): BodyProps[] => {
    return rawList.map((item) => {
      const gotoEncounter = () => {
        switch (item.type) {
          case "note":
            return navigate(currentPath.replace("vitals", "notes"));
          case "intervention":
            const note = item?.noteContent?.split(",") || "";
            API.kore.readings
              .getSingleReading(
                note[1] || "",
                Session.crackCookieToken("coordinator")
              )
              .then((reading) => {
                readingsDispatch({ type: "UPDATE_READING", payload: reading });
                navigate(
                  `/patient/history/${associateReadingType(note[0] || "kk")
                    .toLowerCase()
                    .replace(" ", "")}/details/${memberId}`
                );
              });
            return;
          case "readings review":
            const noteReading = item?.noteContent?.split(",") || "";
            API.kore.readings
              .getSingleReading(
                noteReading[1] || "",
                Session.crackCookieToken("coordinator")
              )
              .then((reading) => {
                readingsDispatch({ type: "UPDATE_READING", payload: reading });
                navigate(
                  `/patient/history/${associateReadingType(
                    noteReading[0] || "kk"
                  )
                    .toLowerCase()
                    .replace(" ", "")}/details/${memberId}`
                );
              });
            return;
          case "profile change":
            if (item.noteContent === "Management details updated") {
              return navigate(currentPath.replace("vitals", "details"));
            } else if (item.noteContent === "Profile updated") {
              return navigate(currentPath.replace("vitals", "profile"));
            } else if (item.noteContent === "Data exported") {
              return navigate(currentPath.replace("vitals", "export"));
            } else if (item.noteContent?.slice(0, 6) === "Device") {
              return navigate(currentPath.replace("vitals", "devices"));
            } else if (item.noteContent === "Display options updated") {
              return navigate(currentPath.replace("vitals", "options"));
            }
        }
      };
      return {
        cells: [
          {
            content: (
              <StyledTableContainer onClick={gotoEncounter}>
                <p>{`${toLocalTime(item.timestamp)}`}</p>
              </StyledTableContainer>
            ),
            align: "left"
          },
          {
            content: (
              <StyledTableContainer onClick={gotoEncounter}>
                <p>{item.type}</p>
              </StyledTableContainer>
            ),
            align: "left"
          },
          {
            content: (
              <StyledTableContainer onClick={gotoEncounter}>
                <p>
                  {item.type === "intervention"
                    ? "Breach response"
                    : item.noteContent}
                </p>
              </StyledTableContainer>
            ),
            align: "left"
          }
        ]
      };
    });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 175,
        bottom: 75,
        left: 300,
        right: 115,
        overflow: "scroll"
      }}
    >
      <StyledContainer>
        <EncounterTimer />
        <StyledSubContainer>
          {patientReadings.map(({ id, type, date, value }: any) => {
            const typeArr = type.split("");

            const initialLetter = typeArr[0]?.toLowerCase();
            typeArr.splice(0, 1, initialLetter);
            return config?.previewOptions[
              typeArr.join("").replaceAll(" ", "")
            ] ? (
              <StyledLastReadingSquare
                key={id}
                onClick={() => {
                  gotoReadings(type);
                }}
              >
                <StyledReadingText>
                  {t(`u-${type.toLowerCase().replace(" ", "-")}`)}
                </StyledReadingText>
                <StyledReadingInfoText>{value}</StyledReadingInfoText>
                <p>{date}</p>
              </StyledLastReadingSquare>
            ) : (
              ""
            );
          })}
          {config && <div></div>}
        </StyledSubContainer>
        {totalEncounterList !== 0 && (
          <Stack>
            <SimpleGrid
              headers={encounterTableHeaders}
              body={generateList(encounterTableBody || [])}
              manualRows={true}
            />
          </Stack>
        )}
      </StyledContainer>
    </Box>
  );
}
